import _ from 'lodash'
import type {
  ApplicationBundleEntity,
  Edge,
} from '../generated/server-types/entity/applicationBundleEntity'
import { EntitySource, Store } from '../models/store'

// Raw bundle JSON edge: a DELETE overlay entry may carry only _operation.
interface BundleEdge extends Partial<Edge> {
  _operation?: string
}

type GeneratedBundle = NonNullable<ApplicationBundleEntity['applicationBundle']>

interface BundleJson {
  applicationBundle: Omit<GeneratedBundle, 'schemas' | 'views'> & {
    schemas?: Record<string, BundleEdge>
    views?: Record<string, BundleEdge>
  }
}

// Only the two Store methods this resolver calls, so shared and native Store subclasses both satisfy it.
interface BundleApi {
  getStore(): Pick<Store, 'findRecords' | 'resolveEntitiesById'>
}

export class ApplicationBundleResolver {
  private api: BundleApi
  private store: Pick<Store, 'findRecords' | 'resolveEntitiesById'>

  constructor(api: BundleApi) {
    this.api = api
    this.store = api.getStore()
  }

  // the resolved bundle is a raw javascript object
  public resolve(bundle: BundleJson, sources?: EntitySource[]) {
    const parentEdges = bundle.applicationBundle.extends
    if (_.isEmpty(parentEdges)) {
      return this.resolveSchemasForBundle(bundle).then(() => bundle)
    }
    const ids = _.map(parentEdges, 'entityId')
    return this.store
      .findRecords(ids, {
        shouldCacheRecord: false,
        shouldMaterialize: false,
        shouldResolveMixins: false,
      })
      .then((parentBundles) => {
        let resolvedBundle = _.cloneDeep(parentBundles[0])
        for (let i = 1; i < parentBundles.length; ++i) {
          resolvedBundle = this.mergeBundles(resolvedBundle, parentBundles[i])
        }
        resolvedBundle = this.mergeBundles(resolvedBundle, bundle)
        return this.resolveSchemasForBundle(resolvedBundle, sources).then(() => resolvedBundle)
      })
  }

  private mergeBundles(baseBundle: BundleJson, overlayBundle: BundleJson) {
    mergeProperty(baseBundle, overlayBundle, 'applicationBundle.extends')
    mergeProperty(baseBundle, overlayBundle, 'applicationBundle.initialRoute')
    mergeProperty(baseBundle, overlayBundle, 'applicationBundle.name')
    baseBundle.applicationBundle.schemas = mergeMaps(
      baseBundle.applicationBundle.schemas,
      overlayBundle.applicationBundle.schemas
    )
    baseBundle.applicationBundle.views = mergeMaps(
      baseBundle.applicationBundle.views,
      overlayBundle.applicationBundle.views
    )
    return baseBundle
  }

  private resolveSchemasForBundle(bundle: BundleJson, sources?: EntitySource[]) {
    const namespace = 'schemas'
    const mappings = bundle.applicationBundle[namespace]
    const edges = _.values(mappings)
    const ids = _.map(edges, 'entityId')
    return this.store.resolveEntitiesById(ids, sources)
  }
}

function mergeMaps(map1: Record<string, BundleEdge> = {}, map2: Record<string, BundleEdge> = {}) {
  const mergedProperties = { ...map1, ...map2 }
  const results: Record<string, BundleEdge> = {}
  _.forEach(mergedProperties, (value, key) => {
    if (value._operation !== 'DELETE') {
      results[key] = value
    }
  })
  return results
}

function mergeProperty(baseBundle: BundleJson, overlayBundle: BundleJson, key: string) {
  const baseBundleValue = _.get(baseBundle, key)
  const overlayValue = _.get(overlayBundle, key, baseBundleValue)
  _.set(baseBundle, key, overlayValue)
}
